import { useMutation, useQuery } from '@tanstack/react-query'
import axiosClient from '../../axios'
import { PlanType, EnterpriseFormData, UserSubscription } from '../../../types/subscription'

interface SelectPlanPayload {
  plan: PlanType
  modules?: string[]
  meta?: {
    establishmentType?: string
    teamSize?: string
    analysisVolume?: string
  }
}

interface SelectPlanResponse {
  success: boolean
  subscription: UserSubscription
}

/**
 * Hook to select a subscription plan
 */
export const useSelectPlan = () => {
  return useMutation({
    mutationKey: ['selectPlan'],
    mutationFn: async (payload: SelectPlanPayload): Promise<SelectPlanResponse> => {
      const response = await axiosClient.post<SelectPlanResponse>('/subscriptions/select', {
        plan: payload.plan,
        modules: payload.modules,
      })
      return response.data
    },
  })
}

/**
 * Hook to submit enterprise plan request
 */
export const useSubmitEnterpriseRequest = () => {
  return useMutation({
    mutationKey: ['submitEnterpriseRequest'],
    mutationFn: async (formData: EnterpriseFormData): Promise<{ success: boolean }> => {
      const response = await axiosClient.post('/subscriptions/enterprise-request', formData)
      return response.data
    },
  })
}

/**
 * Hook to get current user subscription
 */
export const useGetUserSubscription = () => {
  return useQuery({
    queryKey: ['userSubscription'],
    queryFn: async (): Promise<UserSubscription | null> => {
      try {
        const response = await axiosClient.get<{ subscription: UserSubscription | null }>('/subscriptions/current')
        return response.data.subscription
      } catch (error) {
        return null
      }
    },
  })
}

/**
 * Hook to upgrade/change plan
 */
export const useUpgradePlan = () => {
  return useMutation({
    mutationKey: ['upgradePlan'],
    mutationFn: async (payload: SelectPlanPayload): Promise<SelectPlanResponse> => {
      const response = await axiosClient.post<SelectPlanResponse>('/subscriptions/select', {
        plan: payload.plan,
      })
      return response.data
    },
  })
}

/**
 * Hook to cancel subscription
 */
export const useCancelSubscription = () => {
  return useMutation({
    mutationKey: ['cancelSubscription'],
    mutationFn: async (): Promise<{ success: boolean }> => {
      const response = await axiosClient.post('/users/subscription/cancel')

      return response.data
    },
  })
}

/**
 * Hook to save user's selected modules
 */
export const useSaveModules = () => {
  return useMutation({
    mutationKey: ['saveModules'],
    mutationFn: async (modules: string[]): Promise<{ success: boolean }> => {
      const response = await axiosClient.post<{ success: boolean }>('/subscriptions/modules/save', {
        modules,
      })
      return response.data
    },
  })
}
